import { useState, useRef, useEffect } from "react";
import { ToolsShell, SensorNotice, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Metal Detector — renamed from "Magnetic Stud Finder" for honesty.
// Detects FERROUS metal via magnetometer; does NOT find studs themselves
// (only nails/screws driven into them). UI is unambiguous about that.
//
// Approach: read magnetometer continuously, compute total magnitude,
// subtract a baseline (set when phone is moved away from any metal), and
// show the deviation. Smoothed with a moving average.

const SMOOTH_WINDOW = 5;

export default function MetalDetector() {
  const [state, setState] = useState("idle");
  const [magnitude, setMagnitude] = useState(0);
  const [baseline, setBaseline] = useState(null);
  const [peakHold, setPeakHold] = useState(0);
  const sensorRef = useRef(null);
  const handlerRef = useRef(null);
  const smoothBuf = useRef([]);

  const enable = async () => {
    setState("requesting");

    if ("Magnetometer" in window) {
      try {
        const sensor = new window.Magnetometer({ frequency: 30 });
        sensor.addEventListener("reading", () => {
          const x = sensor.x || 0, y = sensor.y || 0, z = sensor.z || 0;
          const m = Math.sqrt(x * x + y * y + z * z);
          smoothBuf.current.push(m);
          if (smoothBuf.current.length > SMOOTH_WINDOW) smoothBuf.current.shift();
          const smoothed = smoothBuf.current.reduce((s, v) => s + v, 0) / smoothBuf.current.length;
          setMagnitude(smoothed);
          setPeakHold((p) => Math.max(p, smoothed));
        });
        sensor.addEventListener("error", () => setState("unsupported"));
        sensor.start();
        sensorRef.current = sensor;
        setState("ok");
        return;
      } catch {}
    }

    // Fallback: no magnetometer access. Tell the user.
    setState("unsupported");
  };

  useEffect(() => () => {
    if (sensorRef.current && sensorRef.current.stop) sensorRef.current.stop();
    if (handlerRef.current) window.removeEventListener("deviceorientationabsolute", handlerRef.current, true);
  }, []);

  const deviation = baseline != null ? magnitude - baseline : 0;
  // Bar scaling: deviations of 10+ μT are "strong"; cap at 100.
  const barPct = Math.min(100, (Math.abs(deviation) / 50) * 100);

  return (
    <ToolsShell title="Metal Detector">
      <div style={{ padding: "12px 14px", background: "#1F1418", border: "1px solid #E0584E33", borderRadius: 10, fontSize: 12, color: "#D9A39E", marginBottom: 16 }}>
        <strong>Detects ferrous metal only.</strong> Finds nails/screws driven into studs, electrical conduit, or other iron/steel. Won't find aluminum studs, plastic boxes, or wood-only construction. For load-bearing work, use a proper stud finder.
      </div>

      {state !== "ok" && (
        <div style={{ textAlign: "center", marginTop: 10 }}>
          <p style={{ fontSize: 14, color: "#C8CADF", marginBottom: 16 }}>
            Tap Start, then move the phone slowly across a surface. Strong magnetic deviations highlight ferrous metal underneath.
          </p>
          <button onClick={enable} style={primaryBtn}>Start</button>
          {state === "unsupported" && (
            <div style={{ marginTop: 14, padding: "14px 16px", background: "#13141F", border: "1px solid #262839", borderRadius: 10, fontSize: 13, color: "#C8CADF", lineHeight: 1.55, textAlign: "left" }}>
              <strong style={{ color: "#F8B84E" }}>Magnetometer not exposed (iOS limitation).</strong> Your browser doesn't expose raw magnetometer readings. The Magnetic Field tool's Compass mode still works for detecting strong magnets (rare-earth stack at close range), but this tool needs magnitude readings to be useful, so it's disabled on iOS.
              <div style={{ marginTop: 8, fontSize: 12, color: "#5B5E7A" }}>
                Chrome on Android typically exposes magnetometer values. iOS Safari and iOS Chrome do not.
              </div>
            </div>
          )}
          {state !== "unsupported" && (
            <SensorNotice state={state === "idle" ? "prompt" : state === "requesting" ? "prompt" : state} what="Magnetometer" />
          )}
        </div>
      )}

      {state === "ok" && (
        <>
          <div style={{ background: "#13141F", border: `1px solid ${TOOLS_ACCENT}33`, borderRadius: 14, padding: "18px 20px" }}>
            <div style={{ fontSize: 13, color: "#9A9DBE", marginBottom: 8 }}>
              Deviation from baseline
            </div>
            <div style={{ position: "relative", height: 28, background: "#0F1018", borderRadius: 14, overflow: "hidden" }}>
              <div style={{
                width: `${barPct}%`,
                height: "100%",
                background: `linear-gradient(90deg, ${TOOLS_ACCENT}, #F8B84E)`,
                transition: "width 0.1s linear",
              }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4 }}>Current</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums" }}>{magnitude.toFixed(1)} μT</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4 }}>Δ baseline</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: Math.abs(deviation) > 5 ? "#F8B84E" : "#9A9DBE", fontVariantNumeric: "tabular-nums" }}>
                  {baseline == null ? "—" : `${deviation > 0 ? "+" : ""}${deviation.toFixed(1)}`}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4 }}>Peak</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#9A9DBE", fontVariantNumeric: "tabular-nums" }}>{peakHold.toFixed(1)}</div>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
            <button onClick={() => setBaseline(magnitude)} style={{ background: "#1C1E2E", color: TOOLS_ACCENT, border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              Set baseline here
            </button>
            <button onClick={() => { setPeakHold(0); }} style={{ background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, cursor: "pointer" }}>
              Reset peak
            </button>
            {baseline != null && (
              <button onClick={() => setBaseline(null)} style={{ background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, cursor: "pointer" }}>
                Clear baseline
              </button>
            )}
          </div>

          <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 14 }}>
            <strong>How to use:</strong> 1) Move phone away from any metal, tap "Set baseline here". 2) Sweep slowly across the wall ~1 inch from surface. Deviations of 5+ μT usually mean metal underneath. Keep sweeps slow — gyro noise increases with fast motion.
          </p>
        </>
      )}
    </ToolsShell>
  );
}
